import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The trunk card and its SIP activity — what replaced the SIP-credentials card on Numbers & SIP
 * (plan-008 ST-UN, design §22.14, R-UI-1, P10), laid out as the prototype draws that pair: a facts
 * card and a signalling card side by side (`WeTalk.dc.html` line 2529).
 *
 * **Status only as observed.** The card shows the trunk's state, the last connection check in the
 * design's words, and the last inbound and outbound leg with how each ended — never a green light
 * inferred from configuration. Encryption is shown twice on purpose: what was asked for, and what the
 * last conversation actually used.
 *
 * **Registration (plan-008 ST-KM).** A trunk whose provider sends calls to WeTalk's registration shows the
 * registration front's last report — "Registered with your provider since …", "Registration failed (SIP
 * 403)" — and offers no inbound sign-in details, which such a trunk does not use.
 *
 * **An inbound-only trunk (plan-015 SIP, ADR 0046)** has no outbound half: its card says so in one row instead of
 * host, username and password, says there is nothing to check (its first answered call verifies it — "Waiting for
 * the first call" until then), and offers no "Check connection"; its test call is "I call the agent" only.
 *
 * **No password on this screen.** The password row is a mask with "Replace"; the trunk view has no
 * password field to read. The inbound sign-in password is shown once, in its own dialog.
 *
 * **Writes are offered to Owners and Admins only** (`sip_trunk.manage`, the trunk routes' own role list,
 * ST-90): a Member or a Viewer reads the card — status, numbers, activity — and sees who manages it
 * instead of Replace, sign-in details, the number's agent picker and the action row.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  Icon,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { MASKED_PASSWORD } from "../../api/channel-view.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, voiceAgentListQuery } from "../../api/query.js";
import {
  isInboundOnlyTrunkView,
  type SipTrunkCheckView,
  type SipTrunkInboundAuthView,
  type SipTrunkSettingsView,
  type SipTrunkView,
} from "../../api/slice/sip-trunk.js";
import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { sipResponseOf } from "./number-display.js";
import {
  HD_SENTENCE,
  INBOUND_DELIVERY_WORD,
  INBOUND_NOT_READY_WORD,
  INBOUND_ONLY_STATUS_SENTENCE,
  INBOUND_ONLY_TRUNK_STATE_WORD,
  REGISTRATION_INBOUND_SENTENCE,
  MEDIA_ENCRYPTION_WORD,
  TRANSPORT_WORD,
  TRUNK_STATE_WORD,
  checkKindSentence,
  checkKindTone,
  checkOutcomeWords,
  codecWords,
  endReasonWords,
  eventLineOf,
  inboundOnlyOutboundSentence,
  offeredCodecWords,
  registrationLineOf,
  registrationTone,
  type SipEdgeAddressView,
} from "./sip-trunk-copy.js";
import { ReplacePasswordDialog } from "./sip-trunk-dialog.js";
import { InboundAuthDialog } from "./sip-trunk-inbound.js";
import { SIP_TRUNK_QUERY_KEY, sipTrunkEventQuery } from "./sip-trunk-query.js";
import { SipTrunkTestCallDialog } from "./sip-trunk-test-call.js";

type TagStatus = "positive" | "caution" | "critical" | "muted";

const STATE_STATUS: Readonly<Record<SipTrunkView["state"], TagStatus>> = {
  verified: "positive",
  unverified: "caution",
  disabled: "muted",
};

export interface SipTrunkCardsProps {
  readonly trunk: SipTrunkView;
  readonly settings: SipTrunkSettingsView | undefined;
  readonly onEdit: (trunk: SipTrunkView) => void;
  /** A line the page shows under the card after a save ("Saved. Check the connection…"). */
  readonly notice: string | null;
}

/** One trunk: its card, then its SIP activity — two grid cells, like the prototype's pair. */
export function SipTrunkCards({ trunk, settings, onEdit, notice }: SipTrunkCardsProps): ReactNode {
  usePortalTranslation("operations");
  return (
    <>
      <SipTrunkCard trunk={trunk} settings={settings} onEdit={onEdit} notice={notice} />
      <SipTrunkActivity trunk={trunk} />
    </>
  );
}

function edgeOf(settings: SipTrunkSettingsView | undefined): SipEdgeAddressView | null {
  return settings === undefined ? null : { sipEdgeHost: settings.sipEdgeHost, sipEdgeAddress: settings.sipEdgeAddress };
}

type Open = "replace_password" | "test_call" | "delete" | null;

function SipTrunkCard({ trunk, settings, onEdit, notice }: SipTrunkCardsProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<Open>(null);
  const [checked, setChecked] = useState<SipTrunkCheckView | null>(null);
  const [problem, setProblem] = usePortalMessage();
  const [status, setStatus] = usePortalMessage();
  const [issued, setIssued] = useState<SipTrunkInboundAuthView | null>(null);
  const edge = edgeOf(settings);
  // Offered, never allowed: the trunk routes refuse a Member or a Viewer on their own (SIP_TRUNK_WRITE).
  const member = useMemberCan();
  const canManage = member.can("sip_trunk.manage");
  const inboundOnly = isInboundOnlyTrunkView(trunk);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
      queryClient.invalidateQueries({ queryKey: [...SIP_TRUNK_QUERY_KEY.sipTrunkEvent, trunk.sipTrunkId] }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
    ]);
  };

  const failed = (fallback: () => string) => (raised: unknown) => {
    setStatus(null);
    setProblem(() => (apiErrorText(raised, fallback())));
  };

  const check = useMutation({
    mutationFn: () => gateway.checkSipTrunk(trunk.sipTrunkId),
    onMutate: () => {
      setProblem(null);
      setChecked(null);
    },
    onSuccess: (made) => {
      setChecked(made);
    },
    onError: failed(() => portalText("operations", "the_connection_could_not_be_checked_nothing_was_sent_to_yo_e368b98d")),
    onSettled: refresh,
  });

  const toggle = useMutation({
    mutationFn: () =>
      trunk.state === "disabled" ? gateway.enableSipTrunk(trunk.sipTrunkId) : gateway.disableSipTrunk(trunk.sipTrunkId),
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: (made) => {
      setStatus(
        () => (made.state === "disabled"
          ? portalText("operations", "disabled_its_numbers_stop_answering_and_nothing_is_dialled_3b724372")
          : portalText("operations", "enabled_check_the_connection_or_place_a_test_call_to_verif_eb910a23")),
      );
    },
    onError: failed(() => portalText("operations", "the_trunk_could_not_be_changed_nothing_has_changed_8e12d88f")),
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: () => gateway.deleteSipTrunk(trunk.sipTrunkId),
    onSuccess: () => {
      setOpen(null);
    },
    onError: (raised: unknown) => {
      setOpen(null);
      failed(() => portalText("operations", "the_trunk_could_not_be_deleted_nothing_has_changed_0ffb5caf"))(raised);
    },
    onSettled: refresh,
  });

  const inbound = useMutation({
    mutationFn: () => gateway.createSipTrunkInboundAuth(trunk.sipTrunkId),
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: (made) => {
      setIssued(made);
    },
    onError: failed(() => portalText("operations", "the_sign_in_details_could_not_be_created_nothing_has_chang_0c1750e9")),
    onSettled: refresh,
  });

  const checkWords = checked === null ? null : checkOutcomeWords(checked.outcome, trunk, edge);

  return (
    <div className="nb-card nb-card-wide">
      <div className="nb-card-head">
        <p className="nb-card-title">{trunk.displayName}</p>
        <Tag status={STATE_STATUS[trunk.state]}>
          {(inboundOnly ? INBOUND_ONLY_TRUNK_STATE_WORD : TRUNK_STATE_WORD)[trunk.state]}
        </Tag>
      </div>
      {notice === null ? null : (
        <p className="nb-saved" role="status">
          {notice}
        </p>
      )}
      <dl className="nb-facts">
        {inboundOnly ? (
          <>
            <dt className="nb-term">{portalText("operations", "outbound_2c7807c0")}</dt>
            <dd className="nb-def">{inboundOnlyOutboundSentence(canManage)}</dd>
          </>
        ) : (
          <>
            <dt className="nb-term">{portalText("operations", "host_3960ec4c")}</dt>
            <dd className="nb-def">
              {trunk.host}:{String(trunk.port)} · {TRANSPORT_WORD[trunk.transport]}
            </dd>

            <dt className="nb-term">{portalText("operations", "username_84c29015")}</dt>
            <dd className="nb-def">{trunk.authUsername}</dd>

            <dt className="nb-term">{portalText("operations", "password_8be3c943")}</dt>
            <dd className="nb-def">
              <span>{MASKED_PASSWORD}</span>{" "}
              {canManage ? (
                <button
                  type="button"
                  className="nb-link"
                  onClick={() => {
                    setOpen("replace_password");
                  }}
                >
                  {portalText("operations", "replace_a7cf7b25")}</button>
              ) : null}
            </dd>
          </>
        )}

        <dt className="nb-term">{portalText("operations", "numbers_7e53821f")}</dt>
        <dd className="nb-def">
          <TrunkNumbers trunk={trunk} canManage={canManage} onChanged={refresh} onProblem={setProblem} />
        </dd>

        <dt className="nb-term">{portalText("operations", "codecs_dcce4ae4")}</dt>
        <dd className="nb-def">
          {offeredCodecWords(trunk.codecOffered)}
          <span className="nb-secret-ref">{HD_SENTENCE()}</span>
        </dd>

        <dt className="nb-term">{portalText("operations", "encryption_0af149c2")}</dt>
        <dd className="nb-def">
          {MEDIA_ENCRYPTION_WORD[trunk.mediaEncryption]}
          <span className="nb-secret-ref">
            {trunk.lastLegEncryption === null
              ? portalText("operations", "no_conversation_has_used_it_yet_3bc9ff82")
              : portalText("operations", "the_last_conversation_used_value_value_bb16386b", { v0: String(TRANSPORT_WORD[trunk.lastLegEncryption.transport]), v1: String(trunk.lastLegEncryption.mediaEncryption === "srtp"
                  ? portalText("operations", "with_encrypted_audio_srtp_56322ed0")
                  : portalText("operations", "with_unencrypted_audio_55861d4a")) })}
          </span>
        </dd>

        <dt className="nb-term">{portalText("operations", "status_bae7d5be")}</dt>
        <dd className="nb-def">
          {inboundOnly ? (
            INBOUND_ONLY_STATUS_SENTENCE()
          ) : trunk.lastCheck === null ? (
            portalText("operations", "not_checked_yet_8ea64e33")
          ) : (
            <span className={`nb-check-${checkKindTone(trunk.lastCheck.kind)}`}>
              {checkKindSentence(trunk.lastCheck.kind, trunk, edge)}
            </span>
          )}
          <span className="nb-secret-ref">
            {trunk.lastCheck === null ? "" : portalText("operations", "checked_value_dd1689e9", { v0: String(trunk.lastCheck.atText) })}
            {trunk.lastInbound === null
              ? portalText("operations", "no_incoming_conversation_yet_984afaca")
              : portalText("operations", "last_incoming_value_value_f6efd11c", { v0: String(trunk.lastInbound.atText), v1: String(endReasonWords(trunk.lastInbound.endReason)) })}
            {inboundOnly
              ? ""
              : trunk.lastOutbound === null
                ? portalText("operations", "no_outgoing_conversation_yet_ef82e555")
                : portalText("operations", "last_outgoing_value_value_35679450", { v0: String(trunk.lastOutbound.atText), v1: String(endReasonWords(trunk.lastOutbound.endReason)) })}
          </span>
        </dd>

        <dt className="nb-term">{portalText("operations", "calls_in_ce1dd38e")}</dt>
        <dd className="nb-def">
          {INBOUND_DELIVERY_WORD[trunk.inboundDelivery]}
          {trunk.inboundDelivery === "registration" ? (
            <span className={`nb-secret-ref nb-check-${registrationTone(trunk.registration)}`}>
              {registrationLineOf(trunk.registration)}
            </span>
          ) : null}
        </dd>

        <dt className="nb-term">{portalText("operations", "receive_965fb2dd")}</dt>
        <dd className="nb-def">
          {trunk.inboundReadiness.ready
            ? portalText("operations", "ready_to_receive_conversations_on_its_connected_numbers_44b261b4")
            : INBOUND_NOT_READY_WORD[trunk.inboundReadiness.reason]}
          <span className="nb-secret-ref">
            {trunk.inboundDelivery === "registration"
              ? REGISTRATION_INBOUND_SENTENCE()
              : trunk.inboundAuthUsername === null
                ? portalText("operations", "your_provider_signs_in_to_wetalk_with_details_wetalk_creat_e471c5d0")
                : portalText("operations", "your_provider_signs_in_to_wetalk_as_value_the_password_was_4442b19b", { v0: String(trunk.inboundAuthUsername) })}
          </span>
          {canManage && trunk.inboundDelivery === "direct" ? (
            <button
              type="button"
              className="nb-link"
              disabled={inbound.isPending}
              onClick={() => {
                inbound.mutate();
              }}
            >
              {trunk.inboundAuthUsername === null ? portalText("operations", "create_sign_in_details_10ce7959") : portalText("operations", "replace_sign_in_details_eb604bc1")}
            </button>
          ) : null}
        </dd>
      </dl>

      {checkWords === null || checked === null ? null : (
        <div className={`nb-outcome nb-outcome-${checkWords.tone}`} role="status">
          <p className="nb-outcome-line">{checkWords.sentence}</p>
          {checkWords.tls === null ? null : <p className="nb-outcome-line">{checkWords.tls}</p>}
          <p className="nb-secret-ref">{portalText("operations", "checked_aca936ba")}{checked.checkedAtText}{portalText("operations", "checking_is_free_and_rings_nobody_bd701acf")}</p>
        </div>
      )}
      {status === null ? null : (
        <p className="nb-note" role="status">
          {status}
        </p>
      )}
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}

      {canManage ? null : member.whyNot("sip_trunk.manage") === null ? null : (
        <p className="nb-note">{member.whyNot("sip_trunk.manage")}</p>
      )}
      {!canManage ? null : (
        <div className="nb-card-actions">
          {inboundOnly ? null : (
            <Button
              variant="secondary"
              disabled={check.isPending || trunk.state === "disabled"}
              onClick={() => {
                check.mutate();
              }}
            >
              {check.isPending ? portalText("operations", "checking_820d6004") : portalText("operations", "check_connection_14caeff0")}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={trunk.state === "disabled"}
            onClick={() => {
              setOpen("test_call");
            }}
          >
            <Icon glyph={GLYPH.phoneOutgoing} size="md" />
            {portalText("operations", "test_call_ab98c76c")}</Button>
          <Button
            variant="secondary"
            onClick={() => {
              onEdit(trunk);
            }}
          >
            {portalText("operations", "edit_5301648d")}</Button>
          <Button
            variant="ghost"
            disabled={toggle.isPending}
            onClick={() => {
              toggle.mutate();
            }}
          >
            {trunk.state === "disabled" ? portalText("operations", "enable_20063ad9") : portalText("operations", "disable_9a7d4e06")}
          </Button>
          <span className="nb-danger-wrap">
            <Button
              variant="ghost"
              onClick={() => {
                setOpen("delete");
              }}
            >
              {portalText("operations", "delete_f6fdbe48")}</Button>
          </span>
        </div>
      )}

      {open === "replace_password" ? (
        <ReplacePasswordDialog
          trunk={trunk}
          onClose={() => {
            setOpen(null);
          }}
          onReplaced={() => {
            setStatus(() => (portalText("operations", "password_replaced_check_the_connection_or_place_a_test_cal_ae1eab89")));
          }}
        />
      ) : null}
      {open === "test_call" ? (
        <SipTrunkTestCallDialog
          trunk={trunk}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === "delete" ? (
        <Dialog
          open
          title={portalText("operations", "delete_value_8359c184", { v0: String(trunk.displayName) })}
          onClose={() => {
            setOpen(null);
          }}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setOpen(null);
                }}
              >
                {portalText("operations", "keep_it_d9f36924")}</Button>
              <Button
                variant="primary"
                disabled={remove.isPending}
                onClick={() => {
                  remove.mutate();
                }}
              >
                {remove.isPending ? portalText("operations", "deleting_c7ac551e") : portalText("operations", "delete_the_trunk_9deb7a8d")}
              </Button>
            </>
          }
        >
          <p className="c-note">
            {portalText("operations", "its_numbers_are_released_and_stop_answering_its_password_i_804b8163")}</p>
        </Dialog>
      ) : null}
      {issued === null ? null : (
        <InboundAuthDialog
          trunkName={trunk.displayName}
          issued={issued}
          settings={settings}
          onClose={() => {
            // Forgotten here: the password never outlives the dialog.
            setIssued(null);
          }}
        />
      )}
    </div>
  );
}

/** The trunk's numbers, each with its wire form, the caller-ID mark and the agent it reaches. */
function TrunkNumbers({
  trunk,
  canManage,
  onChanged,
  onProblem,
}: {
  readonly trunk: SipTrunkView;
  /** False for a Member or a Viewer: the agent each number reaches is shown, not offered to change. */
  readonly canManage: boolean;
  readonly onChanged: () => Promise<void>;
  readonly onProblem: (problem: string | (() => string) | null) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const bind = useMutation({
    mutationFn: ({ phoneNumberId, voiceAgentId }: { readonly phoneNumberId: string; readonly voiceAgentId: string }) =>
      voiceAgentId === ""
        ? gateway.unbindSipTrunkNumber(trunk.sipTrunkId, phoneNumberId)
        : gateway.bindSipTrunkNumber(trunk.sipTrunkId, phoneNumberId, voiceAgentId).then(() => undefined),
    onMutate: () => {
      onProblem(null);
    },
    onError: (raised: unknown) => {
      onProblem(() => apiErrorText(raised, portalText("operations", "the_number_could_not_be_connected_nothing_has_changed_81ca6ec9")));
    },
    onSettled: onChanged,
  });

  if (trunk.number.length === 0) return <>{canManage ? portalText("operations", "no_numbers_yet_add_them_with_edit_2ebfa753") : portalText("operations", "no_numbers_yet_38cd0879")}</>;
  const voiceAgents = voiceAgentList.data ?? [];
  return (
    <ul className="nb-trunk-numbers">
      {trunk.number.map((one) => {
        const known = voiceAgents.some((each) => each.voiceAgentId === one.voiceAgentId);
        return (
          <li key={one.phoneNumberId} className="nb-trunk-number">
            <span className="nb-trunk-number-e164">
              {one.e164}
              {one.isCallerId ? (
                <>
                  {" "}
                  <Tag tone="outline">{portalText("operations", "caller_id_72217b6d")}</Tag>
                </>
              ) : null}
            </span>
            <span className="nb-secret-ref">{portalText("operations", "your_provider_writes_it_7f8f9af7")}{one.wireForm}</span>
            {!canManage ? (
              <span className="nb-secret-ref">
                {one.voiceAgentId === null
                  ? portalText("operations", "not_connected_to_an_agent_bd9a5c60")
                  : portalText("operations", "answered_by_value_225636b1", { v0: String(one.voiceAgentName ?? portalText("operations", "an_agent_14ff0384")) })}
              </span>
            ) : (
              <label className="nb-inline-select">
                <span className="c-visually-hidden">{portalText("operations", "agent_that_answers_9480f860")}{one.e164}</span>
                <Select
                  value={one.voiceAgentId ?? ""}
                  disabled={bind.isPending}
                  onChange={(event) => {
                    bind.mutate({ phoneNumberId: one.phoneNumberId, voiceAgentId: event.currentTarget.value });
                  }}
                >
                  <option value="">{portalText("operations", "not_connected_to_an_agent_bd9a5c60")}</option>
                  {one.voiceAgentId !== null && !known ? (
                    <option value={one.voiceAgentId}>{one.voiceAgentName ?? portalText("operations", "an_agent_d95187e6")}</option>
                  ) : null}
                  {voiceAgents.map((each) => (
                    <option key={each.voiceAgentId} value={each.voiceAgentId}>
                      {each.displayName}
                    </option>
                  ))}
                </Select>
              </label>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The latest SIP facts of the trunk (design §22.14): masked numbers, the codec, observed or derived. */
function SipTrunkActivity({ trunk }: { readonly trunk: SipTrunkView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const events = useQuery(sipTrunkEventQuery(gateway, trunk.sipTrunkId));
  const nameOf = (voiceAgentId: string | null): string =>
    voiceAgentId === null ? "" : (trunk.number.find((one) => one.voiceAgentId === voiceAgentId)?.voiceAgentName ?? "");

  return (
    <div className="nb-card">
      <p className="nb-card-title nb-card-title-spaced">{portalText("operations", "recent_sip_activity_13b79926")}</p>
      {events.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {apiErrorText(events.error, portalText("operations", "the_activity_could_not_be_read_2749b3b2"))}
        </p>
      ) : events.data === undefined ? (
        <p className="nb-note">{portalText("operations", "reading_the_activity_69558e57")}</p>
      ) : events.data.length === 0 ? (
        <p className="nb-note">{portalText("operations", "nothing_yet_checks_and_conversations_through_this_trunk_ap_91b7b472")}</p>
      ) : (
        <div className="nb-trace nb-scroll-inline">
          <Table caption={portalText("operations", "recent_sip_activity_for_value_13f957cd", { v0: String(trunk.displayName) })}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("operations", "when_769bb19e")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "what_97211a88")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "response_6e617e4f")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "codec_a336769a")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.data.map((one) => {
                const name = nameOf(one.voiceAgentId);
                const answered = one.kind === "answered" || one.codec !== null;
                return (
                  <TableRow key={one.sipTrunkEventId}>
                    <TableCell numeric>{one.atText}</TableCell>
                    <TableCell>
                      {eventLineOf(one)}
                      {name === "" ? null : <span className="nb-secret-ref">{name}</span>}
                      {one.synthesised ? (
                        <>
                          {" "}
                          <Tag tone="outline">{portalText("operations", "recorded_by_wetalk_d6cff43d")}</Tag>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell numeric>{sipResponseOf(one.responseCode)}</TableCell>
                    <TableCell>{answered ? codecWords(one.codec) : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
